import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import gsgLogo from '../../assets/clientLogos/gsg_logo_transparent.png'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/* Hero visual — % discount tag + crawler dots, signalling coupon engine */
const GsgHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="gsg-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="gsg-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#gsg-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* Big % discount tag */}
    <g transform="translate(230 230) rotate(-10)">
      <path d="M-110 -40 L60 -40 L110 0 L60 40 L-110 40 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(0,212,255,0.65)" strokeWidth="2" />
      <circle cx="60" cy="0" r="8" fill="rgba(0,212,255,0.65)" />
      <text x="-90" y="14" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="56" fill="url(#gsg-grad)">%</text>
      <rect x="-30" y="-20" width="80" height="6" rx="3" fill="rgba(255,255,255,0.45)" />
      <rect x="-30" y="-6" width="60" height="6" rx="3" fill="rgba(255,255,255,0.30)" />
      <rect x="-30" y="8" width="50" height="6" rx="3" fill="rgba(255,255,255,0.20)" />
    </g>

    {/* Crawler particles flying in */}
    {[
      [60, 80, '0s'], [400, 90, '0.6s'], [70, 360, '0.3s'],
      [400, 380, '0.9s'], [50, 220, '1.2s'], [410, 230, '1.5s'],
    ].map(([x, y, d], i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="#00d4ff">
        <animate attributeName="opacity" values="0.2;1;0.2" dur="2.4s" begin={d} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
)

const pressLinks = [
  {
    href: 'https://en.wikipedia.org/wiki/Atolls_(company)',
    src: 'Wikipedia',
    title: 'Atolls (formerly Global Savings Group) — company overview',
  },
  {
    href: 'https://www.global-savings-group.com/news/global-savings-group-to-acquire-brand-and-domain-coupons-com/',
    src: 'Global Savings Group',
    title: 'Atolls (formerly Global Savings Group) to acquire brand and domain Coupons.com',
  },
  {
    href: 'https://www.awin.com/us/news-and-events/tips-and-tricks/how-to-advance-your-coupon-code-strategy-with-global-savings-groups-dynamic-coupons',
    src: 'Awin',
    title: "Global Savings Group: advanced coupon code strategy",
  },
]

const GSG = () => (
  <>
    <SEO
      path="/case-studies/global-savings-group"
      title="Global Savings Group — QA Restructure & Test Framework Rebuild"
      description="QA Tech led the organisation-wide QA restructure at Global Savings Group (now Atolls) — migrating Java frameworks to TypeScript, building UI and API automation from scratch, and introducing test management and traceability across teams."
      keywords="Global Savings Group case study, GSG case study, Atolls QA, QA restructure, Java to TypeScript migration, Selenium WebdriverIO, AXIOS API testing, test management, k6 performance, accessibility WCAG"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Global Savings Group — organisation-wide QA restructure',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['Global Savings Group', 'Atolls', 'QA restructure', 'Test automation'],
        url: 'https://qatechltd.com/case-studies/global-savings-group',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>Global Savings Group</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <img src={gsgLogo} alt="Global Savings Group" className="cs-hero__logo" loading="lazy" />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">Global Savings Group</h2>
                <p className="cs-hero__client-sub">Now <strong>Atolls</strong> &mdash; the largest commerce-content network in Europe</p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              Org-wide QA, <span className="cs-hero__title-accent">rebuilt from the inside out.</span>
            </h1>

            <p className="cs-hero__lede">
              We led the QA engineering function at Global Savings Group
              (now Atolls) and ran the project that overhauled the entire
              testing estate &mdash; migrating off Java-based frameworks to
              modern TypeScript stacks, building automation from scratch,
              and embedding test traceability across the org.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_gsg_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <GsgHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">Org-wide</span><span className="cs-stat__label">QA restructure</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Java → TS</span><span className="cs-stat__label">framework migration</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Introduced</span><span className="cs-stat__label">test management &amp; traceability</span></div>
          <div className="cs-stat"><span className="cs-stat__num">From scratch</span><span className="cs-stat__label">UI &amp; API automation</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="Old frameworks, new ambitions.">
          <p>
            GSG sits behind the majority of European publisher sites that
            run coupon and discount sections &mdash; serving deals across
            millions of pages, fed by a core revenue-generating service
            that uses AI to crawl and fetch discounts across the internet.
            Their existing test estate was Java-based, fragmented, and
            slowing the engineering teams down. They needed it rebuilt.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="Strategy" title="A single language. A single playbook.">
          <p>
            We introduced a unified TypeScript-first strategy across the
            organisation &mdash; one stack, one set of conventions, one
            test-management story. POCs were built and battle-tested
            before the migration touched production code, so we could
            move fast without breaking critical revenue paths.
          </p>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="UI automation" title="Selenium + WebdriverIO, built from scratch.">
          <p>
            A new UI/functional test automation framework on Selenium with
            WebdriverIO &mdash; designed for parallel execution, stable
            selectors, and a Page Object architecture engineers could
            actually maintain. Wired to AWS CI/CD so every change gets
            tested as a first-class part of delivery.
          </p>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="API automation" title="AXIOS for REST and GraphQL.">
          <p>
            We set up the API automation framework around AXIOS for both
            REST and GraphQL endpoints &mdash; including the tests behind
            their core revenue-generating service. JEST matchers handled
            assertions, NG-Wiremock handled third-party mocking for
            stable dev-env runs.
          </p>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="Performance &amp; accessibility" title="Beyond functional.">
          <p>
            We added k6 performance testing for backend microservices and
            embedded accessibility checks (WCAG / WCAG 2) into the
            automation framework and manual test plans. Performance and
            inclusion stopped being release-week problems.
          </p>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="Test management" title="Traceability, finally.">
          <p>
            We introduced AIO test management for Jira and rolled out
            traceability across the organisation &mdash; one source of
            truth for test cases, test cycles and release runs. The wider
            engineering org could finally answer the question
            &ldquo;what&rsquo;s actually been tested?&rdquo; without a
            spreadsheet hunt.
          </p>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="Team &amp; ways of working" title="Mentoring, reviews, ceremonies.">
          <p>
            Alongside the build, we owned the code review process for
            tests, set up technical training to bring junior QA engineers
            up to speed on the new TypeScript stack, and embedded testing
            into 3-amigo refinement and sprint ceremonies.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'Org-wide QA restructure', 'TypeScript migration strategy',
            'UI test framework (Selenium + WebdriverIO)',
            'API test framework (AXIOS, REST + GraphQL)',
            'Test management (AIO + Jira)', 'Traceability matrix',
            'Performance testing (k6)', 'Accessibility (WCAG / WCAG 2)',
            'Mocking with NG-Wiremock', 'CI/CD (AWS)',
            'Mentoring &amp; technical training',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'TypeScript', 'JavaScript', 'C#', 'NUnit',
            'Selenium', 'WebdriverIO', 'Cucumber',
            'AXIOS', 'JEST', 'NG-Wiremock',
            'k6', 'AWS CI/CD',
            'Jira', 'AIO Tests',
            'GitHub', 'Bitbucket',
          ]}
        />

        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected coverage of Global Savings Group / Atolls and the
              wider commerce-content business this work supported.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_gsg_press')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. QA Tech is independent and
              not affiliated with Global Savings Group / Atolls.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Inherited a tangled QA estate?</h2>
          <p>We&rsquo;ve untangled bigger ones. Let&rsquo;s talk.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default GSG
